import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnDestroy,
} from '@angular/core';

/**
 * REV-22096 — the animated "constellation" above «Jahresabschluss starten» (Pure View).
 *
 * A one-to-one port of the prototype's hero graphic (`aumico-awf-prototype-v3`, component `EK`): the
 * work areas of a closing orbit a globe-like sphere and every one of them is tied to the core
 * «Jahresabschluss» — the image says what the page is for before a word is read. Nothing in it is
 * data; the labels are the closing's work areas and the motion is decoration.
 *
 * Deliberately NOT a static SVG asset: the prototype's picture is computed per frame (the sphere
 * turns, the bands rotate, dots travel down the spokes towards the core, pills fade as they pass to
 * the far side), and a still frame of it looks like a diagram somebody forgot to finish. The maths is
 * the prototype's, constant for constant, so the two pictures are the same picture.
 *
 * Motion respects `prefers-reduced-motion`: the sphere then stands still at its first frame. The
 * animation loop runs OUTSIDE the Angular zone and pushes one `detectChanges()` per frame into this
 * component only — nothing else on the page is dirtied by it.
 */

/** The prototype's `viewBox` and the sphere inside it. */
const WIDTH = 620;
const HEIGHT = 430;
const CENTER_X = WIDTH / 2;
const CENTER_Y = HEIGHT / 2 + 4;
const RADIUS = 182;

const TEAL = '#0a8394';
const MINT = '#14b8a6';

/** Rotating bands around the sphere and their orbiting dots. */
const BAND_COUNT = 6;
const BAND_RX = RADIUS + 44;
const BAND_RY = RADIUS * 0.34;

/** Below this depth a node sits "behind" the sphere and is drawn as a dot, not a pill. */
const PILL_DEPTH_THRESHOLD = 0.34;

/** How fast the sphere turns (radians per second) and how fast the bands do. */
const SPHERE_SPEED = 0.12;
const BAND_ROTATION_DEG_PER_S = 3;
const BAND_DOT_SPEED = 0.55;

/** The tilt of the sphere towards the viewer, in degrees. */
const TILT_DEG = 14;
const FLATTEN_Y = 0.92;

const PILL_HEIGHT = 32;
const PILL_CHAR_WIDTH = 7.7;
const PILL_PADDING = 40;
const CORE_HALF_WIDTH = 116;
const CORE_HALF_HEIGHT = 23;

const MS_PER_S = 1000;
const FULL_TURN_DEG = 180;

const DEFAULT_LABELS: readonly string[] = [
    'Kontenabstimmung',
    'Belege',
    'Eröffnungsbilanz',
    'MWST',
    'Löhne · AHV',
    'Abgrenzungen',
    'Rückstellungen',
    'Anlagespiegel',
    'Review',
    'OR-Compliance',
    'Wertberichtigungen',
    'Korrekturbuchungen',
    'Jahresrechnung',
    'Abschluss-Agent',
];

/** Where each work area sits on the sphere (latitude/longitude, degrees) and how much it weighs. */
interface SphereSeat {
    lat: number;
    lon: number;
    weight: number;
}

const SEATS: readonly SphereSeat[] = [
    { lat: 50, lon: 0, weight: 1 },
    { lat: 42, lon: 138, weight: 0.8 },
    { lat: 34, lon: 275, weight: 0.8 },
    { lat: 27, lon: 53, weight: 0.8 },
    { lat: 19, lon: 190, weight: 0.8 },
    { lat: 12, lon: 328, weight: 0.8 },
    { lat: 4, lon: 105, weight: 0.8 },
    { lat: -4, lon: 243, weight: 0.8 },
    { lat: -12, lon: 20, weight: 1 },
    { lat: -19, lon: 158, weight: 0.8 },
    { lat: -27, lon: 295, weight: 0.8 },
    { lat: -34, lon: 73, weight: 1 },
    { lat: -42, lon: 210, weight: 1 },
    { lat: -50, lon: 348, weight: 1 },
];

/** Which work areas are drawn connected to each other (index pairs into SEATS). */
const EDGES: ReadonlyArray<readonly [number, number]> = [
    [0, 1],
    [0, 6],
    [0, 11],
    [1, 3],
    [2, 12],
    [5, 6],
    [7, 10],
    [8, 12],
    [8, 13],
    [9, 12],
    [11, 12],
    [3, 4],
    [13, 0],
];

interface ProjectedNode {
    index: number;
    label: string;
    weight: number;
    x: number;
    y: number;
    depth: number;
}

export interface ConstellationBand {
    key: string;
    rotation: number;
    dotX: number;
    dotY: number;
}

export interface ConstellationEdge {
    key: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    opacity: number;
}

export interface ConstellationSpokeDot {
    key: string;
    x: number;
    y: number;
    r: number;
    opacity: number;
}

export interface ConstellationSpoke {
    key: string;
    x: number;
    y: number;
    opacity: number;
    dots: ConstellationSpokeDot[];
}

export interface ConstellationPill {
    key: string;
    kind: 'dot' | 'pill';
    x: number;
    y: number;
    scale: number;
    opacity: number;
    width: number;
    strokeOpacity: number;
    label: string;
}

export interface ConstellationFrame {
    bands: ConstellationBand[];
    edges: ConstellationEdge[];
    spokes: ConstellationSpoke[];
    pills: ConstellationPill[];
    coreHaloRadius: number;
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** The three static "latitude" rings of the sphere, at -45°, 0° and 45°. */
const RING_LATITUDES: readonly number[] = [-45, 0, 45];

@Component({
    selector: 'abcp-closing-constellation',
    templateUrl: './closing-constellation.component.html',
    styleUrls: ['./closing-constellation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClosingConstellationComponent implements AfterViewInit, OnDestroy {
    /** The work areas orbiting the core. The prototype's fourteen by default. */
    @Input() labels: readonly string[] = DEFAULT_LABELS;

    /** What the core says. */
    @Input() coreLabel = 'Jahresabschluss';

    /** Rendered size relative to the 620×430 drawing; the hero uses 1.05, like the prototype. */
    @Input() size = 1;

    readonly width = WIDTH;
    readonly height = HEIGHT;
    readonly centerX = CENTER_X;
    readonly centerY = CENTER_Y;
    readonly radius = RADIUS;
    readonly teal = TEAL;
    readonly mint = MINT;
    readonly bandRx = BAND_RX;
    readonly bandRy = BAND_RY;
    readonly coreHalfWidth = CORE_HALF_WIDTH;
    readonly coreHalfHeight = CORE_HALF_HEIGHT;
    readonly pillHeight = PILL_HEIGHT;

    /** Static rings, computed once — they do not move. */
    readonly rings = RING_LATITUDES.map((latitude) => ({
        key: `ring-${latitude}`,
        cy: CENTER_Y - RADIUS * Math.sin(toRadians(latitude)) * FLATTEN_Y,
        rx: RADIUS * Math.cos(toRadians(latitude)),
        ry: RADIUS * Math.cos(toRadians(latitude)) * 0.24,
    }));

    frame: ConstellationFrame = ClosingConstellationComponent.frameAt(0, DEFAULT_LABELS);

    private rafHandle: number | null = null;
    private startedAt: number | null = null;

    constructor(private readonly zone: NgZone, private readonly cdr: ChangeDetectorRef) {}

    get viewBox(): string {
        return `0 0 ${WIDTH} ${HEIGHT}`;
    }

    get renderedWidth(): number {
        return WIDTH * this.size;
    }

    get renderedHeight(): number {
        return HEIGHT * this.size;
    }

    ngAfterViewInit(): void {
        this.frame = ClosingConstellationComponent.frameAt(0, this.labels);
        this.cdr.detectChanges();

        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            return;
        }
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            const tick = (now: number): void => {
                this.startedAt ??= now;
                const seconds = (now - this.startedAt) / MS_PER_S;
                this.frame = ClosingConstellationComponent.frameAt(seconds, this.labels);
                this.cdr.detectChanges();
                this.rafHandle = window.requestAnimationFrame(tick);
            };
            this.rafHandle = window.requestAnimationFrame(tick);
        });
    }

    ngOnDestroy(): void {
        if (this.rafHandle !== null && typeof window !== 'undefined') {
            window.cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
    }

    trackByKey(_index: number, item: { key: string }): string {
        return item.key;
    }

    /** Text sits 27px in from the pill's left edge, after the dot at 16px. */
    pillTextX(pill: ConstellationPill): number {
        return -pill.width / 2 + 27;
    }

    pillDotX(pill: ConstellationPill): number {
        return -pill.width / 2 + 16;
    }

    /**
     * One frame of the picture at `seconds` since the animation started — the prototype's render
     * function, minus React. Pure: the same second gives the same frame.
     */
    static frameAt(seconds: number, labels: readonly string[]): ConstellationFrame {
        const turn = seconds * SPHERE_SPEED;
        const nodes: ProjectedNode[] = SEATS.map((seat, index) => {
            const projected = ClosingConstellationComponent.project(seat, turn);
            return {
                index,
                label: labels[index] ?? DEFAULT_LABELS[index] ?? '',
                weight: seat.weight,
                ...projected,
            };
        });

        const bands: ConstellationBand[] = Array.from({ length: BAND_COUNT }, (_, index) => {
            const rotation = (FULL_TURN_DEG / BAND_COUNT) * index + seconds * BAND_ROTATION_DEG_PER_S;
            const angle = seconds * BAND_DOT_SPEED + (index * 2 * Math.PI) / BAND_COUNT;
            return {
                key: `band-${index}`,
                rotation,
                dotX: CENTER_X + BAND_RX * Math.cos(angle),
                dotY: CENTER_Y + BAND_RY * Math.sin(angle),
            };
        });

        const edges: ConstellationEdge[] = EDGES.map(([from, to]) => {
            const a = nodes[from];
            const b = nodes[to];
            return {
                key: `edge-${from}-${to}`,
                x1: a.x,
                y1: a.y,
                x2: b.x,
                y2: b.y,
                opacity: 0.04 + 0.12 * Math.min(a.depth, b.depth),
            };
        });

        const spokes: ConstellationSpoke[] = nodes.map((node) => {
            const period = 2.6 + (node.index % 4) * 0.45;
            const dots: ConstellationSpokeDot[] = [0, 0.34, 0.67].map((offset, dotIndex) => {
                const phase = (((seconds / period + offset + node.index * 0.13) % 1) + 1) % 1;
                const eased = phase * phase;
                return {
                    key: `spoke-${node.index}-dot-${dotIndex}`,
                    x: node.x + (CENTER_X - node.x) * eased,
                    y: node.y + (CENTER_Y - node.y) * eased,
                    r: 1.6 + node.depth * 1.2,
                    opacity: Math.sin(Math.PI * phase) * (0.25 + 0.75 * node.depth),
                };
            });
            return {
                key: `spoke-${node.index}`,
                x: node.x,
                y: node.y,
                opacity: 0.05 + 0.2 * node.depth,
                dots,
            };
        });

        // Far side first, near side last, so the pills in front paint over the ones behind.
        const pills: ConstellationPill[] = [...nodes]
            .sort((a, b) => a.depth - b.depth)
            .map((node) => {
                if (node.depth < PILL_DEPTH_THRESHOLD) {
                    return {
                        key: `pill-${node.index}`,
                        kind: 'dot' as const,
                        x: node.x,
                        y: node.y,
                        scale: 1,
                        opacity: 0.35,
                        width: 0,
                        strokeOpacity: 0,
                        label: node.label,
                    };
                }
                const scale = (0.66 + 0.3 * node.depth) * (0.9 + 0.1 * node.weight);
                const width = node.label.length * PILL_CHAR_WIDTH + PILL_PADDING;
                // Fade a pill out as it drifts over the core, so the core's own label stays legible.
                const clearX = Math.max(0, Math.abs(node.x - CENTER_X) - CORE_HALF_WIDTH - (width * scale) / 2);
                const clearY = Math.max(0, Math.abs(node.y - CENTER_Y) - CORE_HALF_HEIGHT - (PILL_HEIGHT * scale) / 2);
                const clearance = Math.min(1, Math.hypot(clearX, clearY) / 40);
                const depthShare = (node.depth - PILL_DEPTH_THRESHOLD) / (1 - PILL_DEPTH_THRESHOLD);
                return {
                    key: `pill-${node.index}`,
                    kind: 'pill' as const,
                    x: node.x,
                    y: node.y,
                    scale,
                    opacity: (0.45 + 0.55 * depthShare) * clearance,
                    width,
                    strokeOpacity: 0.14 + 0.16 * node.depth,
                    label: node.label,
                };
            })
            .filter((pill) => pill.kind === 'dot' || pill.opacity > 0.02);

        return {
            bands,
            edges,
            spokes,
            pills,
            coreHaloRadius: 48 + Math.sin(seconds * 1.6) * 2.5,
        };
    }

    /** A seat on the sphere, turned by `turn` radians and tilted towards the viewer. */
    private static project(seat: SphereSeat, turn: number): { x: number; y: number; depth: number } {
        const lat = toRadians(seat.lat);
        const lon = toRadians(seat.lon) + turn;
        const x = RADIUS * Math.cos(lat) * Math.sin(lon);
        const z = RADIUS * Math.cos(lat) * Math.cos(lon);
        const y = RADIUS * Math.sin(lat);
        const tilt = toRadians(TILT_DEG);
        const tiltedY = y * Math.cos(tilt) - z * Math.sin(tilt);
        const depth = (y * Math.sin(tilt) + z * Math.cos(tilt) + RADIUS) / (2 * RADIUS);
        return { x: CENTER_X + x, y: CENTER_Y - tiltedY * FLATTEN_Y, depth };
    }
}
